import { ApiResponse } from "@/utils/ApiResponse";
import type { QueryKey } from "@tanstack/vue-query";
import { useQueryClient, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import { ModelosVehiculoInterface } from "../../interfaces/modelovehiculosinterfaces/ModeloVehiculo.interface";
import { ToggleVisibilityModeloVehiculoAction } from "../../actions/modeloVehiculoActions";

const toast = useToast();

type ToggleCtx = {
  previousEntries: [QueryKey, unknown][];
};

export function useModeloVehiculoToggleVisib() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (modeloId: string) =>
      ToggleVisibilityModeloVehiculoAction(modeloId),

    onMutate: async (modeloId: string): Promise<ToggleCtx> => {
      await queryClient.cancelQueries({ queryKey: ["ModeloVehiculosList"] });
      const previousEntries = queryClient.getQueriesData({
        queryKey: ["ModeloVehiculosList"],
      });

      queryClient.setQueriesData<ApiResponse<ModelosVehiculoInterface>>(
        { queryKey: ["ModeloVehiculosList"] },
        (old) => {
          if (!old?.data?.data?.modelosVehiculos) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                modelosVehiculos: old.data.data.modelosVehiculos.map((modelo) =>
                  modelo.id === modeloId
                    ? { ...modelo, isVisible: modelo.isVisible === 1 ? 0 : 1 }
                    : modelo
                ),
              },
            },
          };
        }
      );

      return { previousEntries };
    },

    onError: (_error, _modeloId, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error("No se pudo actualizar la visibilidad del modelo.");
    },

    onSuccess: () => {
      toast.success("Visibilidad del modelo actualizada.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ModeloVehiculosList"] });
      void queryClient.refetchQueries({
        queryKey: ["MarcaModeloList"],
        exact: true,
      });
    },
  });

  return {
    ...mutation,
  };
}
